import { I18nService } from './i18n/I18nService';
import { ManifestService } from './services/ManifestService';
import { AudioAssetResolver } from './services/AudioAssetResolver';
import { AudioEngine } from './services/AudioEngine';
import { AudioPreloader } from './services/AudioPreloader';
import { StatePersistence } from './services/StatePersistence';
import { TimerManager } from './services/TimerManager';
import { ToastService } from './services/ToastService';
import { NetworkDetector } from './services/NetworkDetector';
import { GameStateMachine } from './core/GameStateMachine';
import { UIController } from './ui/UIController';
import { KeyboardHandler, KeyboardActions } from './accessibility/KeyboardHandler';
import { AccessibilityManager } from './accessibility/AccessibilityManager';
import { I18N_KEYS } from './i18n/keys';

export class App {
  private i18n: I18nService;
  private manifestService: ManifestService;
  private audioResolver: AudioAssetResolver;
  private audioEngine: AudioEngine;
  private preloader: AudioPreloader;
  private persistence: StatePersistence;
  private timer: TimerManager;
  private toast: ToastService;
  private networkDetector: NetworkDetector;
  private gsm!: GameStateMachine;
  private ui!: UIController;
  private keyboard!: KeyboardHandler;
  private a11y!: AccessibilityManager;

  constructor() {
    this.i18n = new I18nService();
    this.manifestService = new ManifestService();
    this.audioResolver = new AudioAssetResolver();
    this.audioEngine = new AudioEngine();
    this.preloader = new AudioPreloader();
    this.persistence = new StatePersistence();
    this.timer = new TimerManager();
    this.toast = new ToastService();
    this.networkDetector = new NetworkDetector();
  }

  async start(): Promise<void> {
    performance.mark('app-start');

    // Initialize i18n (loads saved locale or defaults to 'vi')
    this.i18n.initialize();

    // Initialize toast service
    this.toast.initialize();

    // Initialize network detector
    this.networkDetector.initialize();

    // Load manifest
    const manifest = await this.manifestService.load();
    performance.mark('manifest-loaded');

    // Initialize audio resolver
    this.audioResolver.initialize(this.manifestService.getAudioBaseUrl());

    // Measure network speed for preloader
    await this.networkDetector.measureSpeed();

    // Create game state machine
    this.gsm = new GameStateMachine(
      this.audioEngine,
      this.audioResolver,
      this.preloader,
      this.persistence,
      this.timer,
      this.networkDetector
    );

    // Initialize accessibility manager
    this.a11y = new AccessibilityManager(this.i18n);
    this.a11y.initialize();

    // Initialize game state machine with manifest
    this.gsm.initialize(manifest);

    // Restore UI locale from saved state
    const state = this.gsm.getState();
    if (state.settings.uiLocale !== this.i18n.getLocale()) {
      this.i18n.setLocale(state.settings.uiLocale);
    }
    
    // Update document title and HTML lang attribute
    this.updateDocumentTitle();

    // Set emergency mode if needed
    if (this.manifestService.isEmergencyMode()) {
      this.gsm.setEmergencyMode(true);
    }

    // Create UI controller
    this.ui = new UIController(
      this.i18n,
      this.gsm,
      this.manifestService,
      this.toast,
      this.a11y
    );

    // Render UI
    this.ui.render();

    // Initialize keyboard handler
    this.keyboard = new KeyboardHandler();
    const actions: KeyboardActions = {
      nextNumber: () => this.ui.handleNextNumber(),
      toggleAutoPlay: () => this.gsm.toggleAutoPlay(),
      openSettings: () => this.ui.openSettings(),
      confirmReset: () => this.ui.handleReset(),
      closeModalOrSkip: () => {
        if (this.ui.isAnyModalOpen()) {
          this.ui.closeAllModals();
        } else {
          this.gsm.forceSkipAudio();
        }
      },
      showHelp: () => this.ui.openKeyboardHelp(),
    };

    this.keyboard.initialize(actions, {
      isModalOpen: () => this.ui.isAnyModalOpen(),
      isPlaying: () => this.gsm.getState().gamePhase === 'PLAYING',
      isAudioPlaying: () => this.gsm.getState().isAudioPlaying,
      isComplete: () => this.gsm.getState().gamePhase === 'COMPLETE',
    });

    // Network events
    this.networkDetector.onOffline = () => {
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_CONNECTION_LOST), 'warning');
      this.gsm.pauseAutoPlay();
    };

    this.networkDetector.onOnline = () => {
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_CONNECTION_RESTORED), 'success');
    };

    // Storage corrupted data notification
    if (!this.persistence.isAvailable()) {
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_STORAGE_LIMIT), 'warning');
    }

    // beforeunload save
    window.addEventListener('beforeunload', () => {
      const currentState = this.gsm.getState();
      if (currentState.gamePhase !== 'IDLE') {
        this.persistence.save({
          version: currentState.version,
          timestamp: new Date().toISOString(),
          gamePhase: currentState.gamePhase,
          settings: currentState.settings,
          session: currentState.session,
        });
      }
    });

    performance.mark('app-ready');
    performance.measure('app-startup', 'app-start', 'app-ready');

    console.log('[App] Loto Caller initialized successfully');
  }

  private updateDocumentTitle(): void {
    const title = this.i18n.t(I18N_KEYS.APP_TITLE);
    document.title = title;
    
    // Update HTML lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = this.i18n.getLocale();
    
    // Update meta description if it exists
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      const description = this.i18n.getLocale() === 'vi' 
        ? 'Xướng Lô Tô - Vietnamese Loto Number Caller'
        : 'Loto Caller - Number Calling Game with Audio';
      metaDescription.setAttribute('content', description);
    }
  }
}
